import { PARTNER_IDS } from "pages/enterpriseDashboard/utils";

export const DEFAULT_SPEAKER_TYPES = [
  { type: "Operator", required: false },
  { type: "Moderator", required: false },
  { type: "Analyst", required: true, useBrackets: true },
  { type: "Company Representative", required: true, useBrackets: true },
  { type: "Shareholder", required: true, useBrackets: true },
  { type: "Video Narrator", required: false },
  { type: "Translator", required: false },
  { type: "Unknown Executive", required: false },
  { type: "Unknown Analyst", required: false },
  { type: "Unknown Shareholder", required: false },
  { type: "Unknown Attendee", required: false },
  { type: "Unknown Speaker", required: false },
];

export const TEGUS_SPEAKER_TYPES = [
  { type: "Advisor", required: false },
  { type: "Tegus Client", required: false },
];

export const THIRD_BRIDGE_SPEAKER_TYPES = [
  { type: "Analyst", required: false, useBrackets: false },
  { type: "Expert", required: false },
];

// `strictInference: true` constrains name-based type inference to exact bracket-
// content / whole-string matches (e.g. "[Analyst]" or "Operator 2") rather than
// substring matches — prevents a position like "Senior Advisor" or "Equity
// Analyst" from being mis-coerced into a type label. Only applied to partners
// whose save formatters embed free-text positions into the saved name.
//
// `genericNumbered: true` switches a partner to anonymized positional labels
// ("Speaker 1", "Speaker 2", ...): structured name/company/position/type entry is
// replaced by a single free-text label per speaker and no format validation runs.
export const SPEAKER_TYPES_MAPPING = {
  "2a9fe8a6-9ace-49fb-b04c-8a936acfe6fd": { types: DEFAULT_SPEAKER_TYPES, strictInference: true }, // aiera
  "9bf61454-565b-4b39-b085-f420a116c97e": { types: DEFAULT_SPEAKER_TYPES, strictInference: true }, // quartr
  "f5c3c46f-f6a6-4436-b35b-f160d80f4a16": { types: TEGUS_SPEAKER_TYPES, strictInference: false }, // tegus
  "d71f9036-ec71-4819-ac1e-807b67d53540": { types: THIRD_BRIDGE_SPEAKER_TYPES, strictInference: false, genericNumbered: true }, // third bridge
};

const DEFAULT_PARTNER_CONFIG = { types: DEFAULT_SPEAKER_TYPES, strictInference: true };

export const getPartnerSpeakerConfig = (assignees) => {
  const partner = assignees.find((assignee) => {
    return PARTNER_IDS.includes(assignee.id) || PARTNER_IDS.includes(assignee.user_id);
  });
  const partnerId = partner?.id || partner?.user_id;

  return SPEAKER_TYPES_MAPPING[partnerId] || DEFAULT_PARTNER_CONFIG;
};

export const getSpeakerTypes = (assignees) => {
  return getPartnerSpeakerConfig(assignees).types;
};

// True for partners (e.g. Third Bridge) that want anonymized positional speaker
// labels instead of the structured name/company/position/type form.
export const usesGenericSpeakers = (assignees = []) => {
  return getPartnerSpeakerConfig(assignees).genericNumbered === true;
};

export const genericSpeakerLabel = (index) => `Speaker ${index + 1}`;

/**
 * Display/save label for a generic-numbered partner speaker.
 * An already-positional "Speaker N" is kept verbatim so list order/gaps cannot
 * renumber it (which would jumble every paragraph tagged with that label).
 */
export const resolveGenericSpeakerLabel = (name, index) => {
  const trimmed = (name || "").trim();

  if (isPositionalSpeakerLabel(trimmed)) return trimmed;
  if (isLegacyAutoSpeakerName(trimmed)) return genericSpeakerLabel(index);

  return trimmed;
};

/**
 * Next unused "Speaker N" for a generic list that may contain gaps
 * (e.g. Speaker 1, Speaker 3 → Speaker 2, not another Speaker 3).
 */
export const nextGenericSpeakerLabel = (existing = []) => {
  const used = new Set();

  existing.forEach((s) => {
    const name = (s?.label || s?.name || s?.speaker || "").trim();
    if (!isPositionalSpeakerLabel(name)) return;
    const n = parseInt(name.replace(/^Speaker\s+/i, ""), 10);
    if (Number.isFinite(n)) used.add(n);
  });

  let n = 1;
  while (used.has(n)) n += 1;

  return `Speaker ${n}`;
};

export const isPositionalSpeakerLabel = (name) =>
  /^Speaker\s+\d+$/i.test((name || "").trim());

// A saved speaker name collapses to a positional "Speaker N" label only when it is blank,
// already positional, or a lambda-baked structured "Name [Position] (Company)" form (which would
// expose a person's name). Bare role words (Moderator/Expert/Analyst...), plain names, and
// deliberate "Name [Role] (Company)" renames are kept and shown as-is.
const SPEAKER_ROLE_WORDS =
  "Analyst|Expert|Specialist|Operator|Moderator|Advisor|Tegus Client|Shareholder|Company Representative|Translator|Video Narrator|Unknown[A-Za-z ]*";
// "Name [Role]" / "Name [Role] (Company)" is a deliberate rename — keep it. The bracket holds a
// role word, which the lambda never pairs with a name (it brackets the job title there), so a
// lambda-baked "Name [Position] (Company)" still collapses.
const NAMED_ROLE_RE = new RegExp(
  `^.+\\s+\\[(?:${SPEAKER_ROLE_WORDS})\\](?:\\s+\\([^)]+\\))?$`,
  "i"
);

export const isLegacyAutoSpeakerName = (name) => {
  const trimmed = (name || "").trim();
  if (!trimmed) return true;
  if (isPositionalSpeakerLabel(trimmed)) return true; // already positional
  if (NAMED_ROLE_RE.test(trimmed)) return false; // deliberate "Name [Role] (Company)" rename
  if (/[[\]()]/.test(trimmed)) return true; // bracketed structured "Name [Position] (Company)" — hides a person name
  return false; // bare role words + plain names — show as-is
};

// Relabel a processed_data.speakers list to positional labels by order, preserving
// deliberate free-text renames. Returns the relabeled list plus tag/name lookup
// maps used to remap transcript paragraph speakers on load.
export const buildGenericSpeakerLabels = (speakers = []) => {
  const byTag = new Map();
  const byName = new Map();
  const relabeled = (speakers || []).map((s, index) => {
    const trimmed = (s?.name || "").trim();
    const isPositional = isPositionalSpeakerLabel(trimmed);
    const label = resolveGenericSpeakerLabel(s?.name, index);
    if (s?.tag) byTag.set(s.tag, label);
    if (s?.name) byName.set(isPositional ? trimmed : s.name, label);
    return { ...s, name: label };
  });

  return { relabeled, byTag, byName };
};

// Relabel legacy structured paragraph speakers on load for anonymized-label partners.
// Positional labels already stored on paragraphs are left unchanged.
export const remapGenericParagraphSpeakers = (transcripts, processedSpeakers = []) => {
  const { byName } = buildGenericSpeakerLabels(processedSpeakers);

  return (transcripts || []).map((t) => {
    if (!t?.speaker) return t;
    const raw = (t.speaker || "").trim();
    if (isPositionalSpeakerLabel(raw)) return t;
    if (byName.has(t.speaker)) {
      return { ...t, speaker: byName.get(t.speaker) };
    }
    return t;
  });
};

const getTag = (length) => (length + 10).toString(36).toUpperCase();

// Find the next available tag
export const getNextAvailableTag = (existingSpeakers) => {
  const existingTags = new Set(existingSpeakers.map(s => s.tag).filter(Boolean));
  let candidateIndex = 0;

  while (true) {
    const candidateTag = getTag(candidateIndex);
    if (!existingTags.has(candidateTag)) {
      return candidateTag;
    }

    candidateIndex++;

    // Prevent infinite loop
    if (candidateIndex > 100) {
      // Fallback: use timestamp if we somehow exhaust all reasonable tags
      return `T${Date.now().toString(36).toUpperCase().slice(-3)}`;
    }
  }
};

export const isInputRequired = (speakerType, speakerTypes = []) => {
  if (!speakerType) {
    return true;
  }

  const match = speakerTypes.find((st) => st.type === speakerType);

  return match ? match.required : false;
};
